import { motion, useReducedMotion } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { MaintenanceScreen, useGetMaintenanceQuery } from '@entities/maintenance';
import { JoinChannelScreen } from '@entities/membership';
import { useGetMeQuery, useHeartbeatPresenceMutation } from '@entities/user';
import { FeedbackPrompt } from '@features/feedback';
import { OrdersPage } from '@pages/OrdersPage';
import { StorePage } from '@pages/StorePage';
import { WalletPage } from '@pages/WalletPage';
import { InvitePage } from '@pages/InvitePage';
import { Screen } from '@shared/ui/Screen';
import { BannedAccountScreen, type BanNotice } from '@shared/ui/BannedAccountScreen';
import { SessionExpiredScreen } from '@shared/ui/SessionExpiredScreen';
import { Spinner } from '@shared/ui/Spinner';
import { BottomNavBar } from '@widgets/BottomNavBar';
import { TopAppBar } from '@widgets/TopAppBar';

// The storefront, wallet and order history are the common customer path and
// stay in the entry bundle. Operational and drill-down screens load only when
// opened, so ordinary customers do not download the entire admin application.
const NumberOrderPage = lazy(() =>
  import('@pages/NumberOrderPage').then(({ NumberOrderPage }) => ({ default: NumberOrderPage })),
);
const NumbersPage = lazy(() =>
  import('@pages/NumbersPage').then(({ NumbersPage }) => ({ default: NumbersPage })),
);
const AdminOrderPage = lazy(() =>
  import('@pages/AdminOrderPage').then(({ AdminOrderPage }) => ({ default: AdminOrderPage })),
);
const AdminUserPage = lazy(() =>
  import('@pages/AdminUserPage').then(({ AdminUserPage }) => ({ default: AdminUserPage })),
);
const AdminBansPage = lazy(() =>
  import('@pages/AdminBansPage').then(({ AdminBansPage }) => ({ default: AdminBansPage })),
);
const AdminResellersPage = lazy(() =>
  import('@pages/AdminResellersPage').then(({ AdminResellersPage }) => ({
    default: AdminResellersPage,
  })),
);
const DeliverOrderPage = lazy(() =>
  import('@pages/DeliverOrderPage').then(({ DeliverOrderPage }) => ({ default: DeliverOrderPage })),
);
const ManageProductsPage = lazy(() =>
  import('@pages/ManageProductsPage').then(({ ManageProductsPage }) => ({
    default: ManageProductsPage,
  })),
);
const MoneyAnalyticsPage = lazy(() =>
  import('@pages/MoneyAnalyticsPage').then(({ MoneyAnalyticsPage }) => ({
    default: MoneyAnalyticsPage,
  })),
);
const PanelPage = lazy(() =>
  import('@pages/PanelPage').then(({ PanelPage }) => ({ default: PanelPage })),
);
const ResellerPage = lazy(() =>
  import('@pages/ResellerPage').then(({ ResellerPage }) => ({ default: ResellerPage })),
);

interface JoinRequirement {
  channel: string;
  joinUrl: string;
}

function getJoinRequirement(error: unknown): JoinRequirement | null {
  if (typeof error !== 'object' || error === null || !('status' in error) || !('data' in error)) {
    return null;
  }

  const result = error as { status?: unknown; data?: unknown };
  if (result.status !== 403 || typeof result.data !== 'object' || result.data === null) return null;

  const data = result.data as { error?: unknown; channel?: unknown; joinUrl?: unknown };
  if (
    data.error !== 'JOIN_CHANNEL_REQUIRED' ||
    typeof data.channel !== 'string' ||
    typeof data.joinUrl !== 'string'
  ) {
    return null;
  }

  return { channel: data.channel, joinUrl: data.joinUrl };
}

function getBanNotice(error: unknown): BanNotice | null {
  if (typeof error !== 'object' || error === null || !('status' in error) || !('data' in error)) {
    return null;
  }
  const result = error as { status?: unknown; data?: unknown };
  if (result.status !== 403 || typeof result.data !== 'object' || result.data === null) return null;
  const data = result.data as { error?: unknown; ban?: unknown };
  if (data.error !== 'USER_BANNED' || typeof data.ban !== 'object' || data.ban === null) return null;
  const ban = data.ban as { reason?: unknown; expiresAt?: unknown };
  if (typeof ban.reason !== 'string' || (ban.expiresAt !== null && typeof ban.expiresAt !== 'string')) {
    return null;
  }
  return { reason: ban.reason, expiresAt: ban.expiresAt };
}

export function App() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const { data: maintenance, isLoading: maintenanceLoading } = useGetMaintenanceQuery();
  // Admins keep full access during maintenance; everyone else sees the notice.
  const {
    data: me,
    isLoading: meLoading,
    isFetching: meFetching,
    error: meError,
    refetch: refetchMe,
  } = useGetMeQuery();
  const [heartbeatPresence] = useHeartbeatPresenceMutation();
  const joinRequirement = getJoinRequirement(meError);
  const banNotice = getBanNotice(meError);

  useEffect(() => {
    if (!me) return;

    const heartbeat = () => {
      if (document.visibilityState === 'visible') {
        void heartbeatPresence().unwrap().catch(() => undefined);
      }
    };

    heartbeat();
    const interval = window.setInterval(heartbeat, 30_000);
    document.addEventListener('visibilitychange', heartbeat);
    window.addEventListener('focus', heartbeat);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', heartbeat);
      window.removeEventListener('focus', heartbeat);
    };
  }, [me, heartbeatPresence]);

  /*
   * Re-check the account whenever the app comes back to the foreground.
   *
   * This is what makes leaving and returning recover on its own: a lapsed
   * session is refetched against the initData Telegram has refreshed in the
   * meantime, so the customer usually never learns anything was wrong. The
   * retry screen below is the fallback for when even that fails.
   *
   * Deliberately outside the heartbeat effect, which bails out when there is
   * no user — exactly the case that needs recovering.
   */
  useEffect(() => {
    const recover = () => {
      if (document.visibilityState === 'visible') void refetchMe();
    };

    document.addEventListener('visibilitychange', recover);
    window.addEventListener('focus', recover);
    return () => {
      document.removeEventListener('visibilitychange', recover);
      window.removeEventListener('focus', recover);
    };
  }, [refetchMe]);

  if (joinRequirement) {
    return (
      <JoinChannelScreen
        {...joinRequirement}
        isChecking={meFetching}
        onRetry={() => void refetchMe()}
      />
    );
  }

  if (banNotice) {
    return (
      <BannedAccountScreen
        ban={banNotice}
        isChecking={meFetching}
        onRetry={() => void refetchMe()}
      />
    );
  }

  if (maintenanceLoading || meLoading) return <Spinner />;

  /*
   * The account could not be loaded and it is not a join prompt.
   *
   * The usual cause is coming back to a Mini App left open for a day: Telegram's
   * initData has a lifetime, and once it lapses every request is rejected. Left
   * unhandled the app rendered on with no user at all, which reads as a broken
   * or empty shop rather than a session that needs refreshing.
   */
  if (meError && !me) {
    return (
      <SessionExpiredScreen isRetrying={meFetching} onRetry={() => void refetchMe()} />
    );
  }

  if (maintenance?.enabled && !me?.isAdmin) {
    return <MaintenanceScreen message={maintenance.message} imageUrl={maintenance.imageUrl} />;
  }

  return (
    <>
      <TopAppBar />

      {/* Routes replace immediately and only the incoming page animates. An
          outgoing presence lifecycle can deadlock with a tab pill's active
          shared-layout projection, leaving `mode="wait"` with no page to show. */}
      <motion.div
        key={location.pathname}
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }
        }
      >
        <Suspense
          fallback={
            <Screen title="Loading">
              <Spinner />
            </Screen>
          }
        >
          <Routes location={location}>
            <Route path="/" element={<StorePage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/services/sms" element={<NumbersPage />} />
            <Route path="/numbers/orders/:orderId" element={<NumberOrderPage />} />
            <Route path="/reseller" element={<ResellerPage />} />
            <Route path="/products" element={<ManageProductsPage />} />
            <Route path="/products/orders/:orderId" element={<DeliverOrderPage />} />
            <Route path="/panel" element={<PanelPage />} />
            <Route path="/panel/analytics" element={<MoneyAnalyticsPage />} />
            <Route path="/panel/orders/:orderId" element={<AdminOrderPage />} />
            <Route path="/panel/users/:telegramId" element={<AdminUserPage />} />
            <Route path="/panel/bans" element={<AdminBansPage />} />
            <Route path="/panel/resellers" element={<AdminResellersPage />} />
            <Route path="*" element={<StorePage />} />
          </Routes>
        </Suspense>
      </motion.div>

      <BottomNavBar />

      {/* Outside the routed area on purpose: the prompt is timed, and a page
          change mid-countdown must not restart or cancel it. */}
      <FeedbackPrompt />
    </>
  );
}
